import BackButton from 'Components/BackButton'
import Breadcrumbs from 'Components/Breadcrumbs'
import CustomLoadingButton from 'Components/CustomLoadingButton'
import DeleteButton from 'Components/DeleteButton'
import DeleteDialog from 'Components/DeleteDialog'
import FieldErrors from 'Components/FieldErrors'
import FileInput from 'Components/FileInput'
import PageTitle from 'Components/PageTitle'
import SaveDialog from 'Components/SaveDialog'
import StyledForm from 'Components/StyledForm'
import StyledLabel from 'Components/StyledLabel'
import RichTextEditor from 'Components/TextEditor'
import { WithLoader } from 'Components/WithLoader'
import { useAuthorityContext } from 'Services/common/Features/Authority/Context/authorityContext'
import {
  changeZoneInfo,
  createZoneInfo,
  deleteZoneInfo,
  fetchZoneInfo,
  uploadZoneInfoImage,
  type ZoneInfoDetailShape
} from 'Services/SalesManagement/Features/ZoneInfo/zoneInfoSlice'
import useSalesManagementBreadcrumbs from 'Services/SalesManagement/Hooks/useSalesManagementBreadcrumbs'
import useTypedSelector, { useAppDispatch } from 'Store'
import { usePreviousLocation } from 'Util/Context/previousLocationContext'
import { SALES_MANAGEMENT_ROOT_URL } from 'Util/constants'
import SnackbarUtils from 'Util/Snackbar'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Tab
} from '@mui/material'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import { unwrapResult } from '@reduxjs/toolkit'
import type Editor from 'ckeditor5-custom-build'
import { type FormEvent, type default as React, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { t } from 'ttag'

export default function ZoneInfoDetailPage() {
  const baseUrl = `${SALES_MANAGEMENT_ROOT_URL}/zone-infos`
  const previousLocation = usePreviousLocation(baseUrl)
  const dispatch = useAppDispatch()
  const { authorities } = useAuthorityContext()

  const instanceAuthorityCodeParam = useParams<{ authority_code: string }>()
    .authority_code as string
  const initialZoneInfoDetails: ZoneInfoDetailShape = {
    id: null,
    created: null,
    modified: null,
    authority_code: '',
    authority_name: '',
    text_fi: '',
    text_sv: '',
    text_en: '',
    image: null
  }
  const [zoneInfoDetails, setZoneInfoDetails] =
    useState<ZoneInfoDetailShape>(initialZoneInfoDetails)
  const [tabValue, setTabValue] = useState('fi')
  const [selectedImage, setSelectedImage] = useState<File | null>(null)

  const fieldErrorsDefaultState = {
    authority_code: [],
    text_fi: [],
    text_sv: [],
    text_en: [],
    image: []
  }

  const [fieldErrors, setFieldErrors] = useState(fieldErrorsDefaultState)
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isDeleteActionLoading, setIsDeleteActionLoading] = useState(false)

  const navigate = useNavigate()
  const formRef = useRef<HTMLFormElement>(null)

  const [fiEditor, setFiEditor] = useState<Editor>()
  const [enEditor, setEnEditor] = useState<Editor>()
  const [svEditor, setSvEditor] = useState<Editor>()

  const isNewInstance = Number.isNaN(Number.parseInt(instanceAuthorityCodeParam, 10))

  const pageTitle = isNewInstance ? t`Create new zone info` : t`Edit zone info`

  const { singleZoneInfoLoading, submitLoading } = useTypedSelector(state => state.zoneInfo)

  // biome-ignore lint/correctness/useExhaustiveDependencies: initialZoneInfoDetails changes on every re-render and should not be used as a hook dependency.
  useEffect(() => {
    if (isNewInstance) {
      setZoneInfoDetails(initialZoneInfoDetails)
    } else {
      dispatch(
        fetchZoneInfo({
          authority_code: instanceAuthorityCodeParam
        })
      )
        .then(unwrapResult)
        .then(res => {
          setZoneInfoDetails(res)
        })
        .catch(() => {
          SnackbarUtils.error(t`There was an error fetching the zone info.`)
        })
    }
  }, [dispatch, isNewInstance, instanceAuthorityCodeParam])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const breadcrumbs = useSalesManagementBreadcrumbs([
    {
      title: t`Zone infos`,
      link: baseUrl
    },
    {
      title: pageTitle
    }
  ])

  const handleSelectChange = (event: SelectChangeEvent, name: keyof ZoneInfoDetailShape) => {
    const { value } = event.target
    setZoneInfoDetails(prevState => ({ ...prevState, [name]: value }))
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    // save editor content between tab changes
    saveEditorContent(tabValue)
    setTabValue(newValue)
  }

  const onSaveClick = () => {
    saveEditorContent(tabValue)
    setIsSaveDialogOpen(true)
  }

  const handleSubmit = () => {
    formRef.current?.requestSubmit()
  }

  const handleImageUpload = (newImage: File) => {
    const formData = new FormData()
    formData.append('image', newImage)

    dispatch(
      uploadZoneInfoImage({
        authority_code: zoneInfoDetails.authority_code,
        formData
      })
    )
      .then(unwrapResult)
      .catch(() => {
        SnackbarUtils.error(t`There was an error uploading the image.`)
      })
  }

  const handleSuccess = (isCreateAction: boolean) => {
    setIsSaveDialogOpen(false)
    navigate(baseUrl)
    if (isCreateAction) {
      SnackbarUtils.success(t`Zone info successfully added.`)
    } else {
      SnackbarUtils.success(t`Zone info successfully updated.`)
    }
  }

  const handleSave = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (isNewInstance) {
      dispatch(createZoneInfo(zoneInfoDetails))
        .then(unwrapResult)
        .then(() => {
          if (selectedImage) {
            handleImageUpload(selectedImage)
          }
          handleSuccess(isNewInstance)
        })
        .catch(error => {
          if (error) {
            setIsSaveDialogOpen(false)
            setFieldErrors({
              ...error
            })
          }
          SnackbarUtils.error(t`There was an error creating the zone info.`)
        })
    } else {
      dispatch(changeZoneInfo(zoneInfoDetails))
        .then(unwrapResult)
        .then(() => {
          if (selectedImage) {
            handleImageUpload(selectedImage)
          }
          handleSuccess(isNewInstance)
        })
        .catch(error => {
          if (error) {
            setIsSaveDialogOpen(false)
            setFieldErrors({
              ...error
            })
          }
          SnackbarUtils.error(t`There was an error editing the zone info.`)
        })
    }
  }

  const onDelete = () => {
    setIsDeleteDialogOpen(!isDeleteDialogOpen)
  }

  const handleDelete = () => {
    setIsDeleteActionLoading(true)
    dispatch(
      deleteZoneInfo({
        authority_code: instanceAuthorityCodeParam
      })
    )
      .then(unwrapResult)
      .then(() => {
        setIsDeleteDialogOpen(false)
        SnackbarUtils.success(t`Zone info successfully deleted.`)
      })
      .catch(() => {
        SnackbarUtils.error(t`There was an error deleting the zone info.`)
      })
      .finally(() => {
        setIsDeleteActionLoading(false)
        navigate(baseUrl)
      })
  }

  const saveEditorContent = (lang: string) => {
    const field = `text_${lang}`
    let editor = fiEditor
    if (lang === 'sv') {
      editor = svEditor
    } else if (lang === 'en') {
      editor = enEditor
    }

    // This means that the user has not visited the tab yet
    if (!editor) {
      return
    }

    const content = editor.getData()
    setZoneInfoDetails(prevState => ({ ...prevState, [field]: content }))
  }

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target
    const selectedFiles = files as FileList
    setSelectedImage(selectedFiles[0])
  }

  const isLoading = singleZoneInfoLoading === 'pending'
  const isSubmitLoading = submitLoading === 'pending'

  return (
    <Box sx={{ width: '100%' }}>
      <Grid container spacing={2} mb={4}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <Breadcrumbs items={breadcrumbs} />
          <BackButton label={t`Back`} link={previousLocation} />
          <Box
            sx={{
              display: 'flex',
              maxWidth: 800,
              justifyContent: 'space-between',
              alignItems: 'start'
            }}
          >
            <PageTitle>{pageTitle}</PageTitle>
            {!isNewInstance && (
              <>
                <DeleteButton onClick={onDelete}>{t`Delete zone info`}</DeleteButton>
                <DeleteDialog
                  open={isDeleteDialogOpen}
                  handleClose={() => setIsDeleteDialogOpen(false)}
                  handleSubmit={handleDelete}
                  loading={isDeleteActionLoading}
                  title={t`Delete zone info`}
                >
                  <Typography component="span" color="text.primary">
                    {t`Are you sure you want to delete the zone info?`}
                  </Typography>
                </DeleteDialog>
              </>
            )}
          </Box>
        </Grid>
      </Grid>
      <WithLoader loading={isLoading}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, lg: 8 }}>
            <StyledForm onSubmit={handleSave} formRef={formRef} isDisabled={isSubmitLoading}>
              <Box>
                <FormControl sx={{ minWidth: 200 }} required>
                  <InputLabel>{t`Authority`}</InputLabel>
                  <Select
                    disabled={!isNewInstance}
                    value={zoneInfoDetails.authority_code}
                    label={t`Authority`}
                    autoWidth
                    onChange={e => handleSelectChange(e, 'authority_code')}
                    error={fieldErrors.authority_code?.length > 0}
                  >
                    {authorities?.map(({ code, name }) => (
                      <MenuItem key={code} value={code}>
                        {name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FieldErrors error={fieldErrors.authority_code} />
                </FormControl>
              </Box>
              <Box>
                <StyledLabel>{t`Image`}</StyledLabel>
                <Box>
                  {zoneInfoDetails.image ? (
                    <img alt={t`Zone info`} style={{ maxWidth: 350 }} src={zoneInfoDetails.image} />
                  ) : (
                    <Typography color="text.secondary">{t`No image added`}</Typography>
                  )}
                </Box>
                <Box>
                  <FileInput
                    selectedValue={selectedImage ? selectedImage.name : ''}
                    label={t`Upload new image`}
                    onChange={handleImageChange}
                  />
                </Box>
              </Box>
              <Box sx={{ my: 4 }}>
                <StyledLabel>{t`Content`}</StyledLabel>
                <TabContext value={tabValue}>
                  <Box sx={{ backgroundColor: 'background.paper' }}>
                    <Paper>
                      <TabList onChange={handleTabChange} aria-label={t`Content tab list`}>
                        <Tab label="FI" value="fi" />
                        <Tab label="SV" value="sv" />
                        <Tab label="EN" value="en" />
                      </TabList>
                    </Paper>
                    <TabPanel sx={{ px: 0, pt: 0 }} value="fi">
                      <Box sx={{ p: 2 }}>
                        <RichTextEditor
                          value={zoneInfoDetails.text_fi ?? ''}
                          setEditor={setFiEditor}
                          placeholder={isNewInstance ? t`Start typing here...` : t`No content`}
                          editor={fiEditor}
                          removePlugins={['ImageInsert', 'ImageResize', 'ImageUpload']}
                        />
                        <FieldErrors error={fieldErrors.text_fi} />
                      </Box>
                    </TabPanel>
                    <TabPanel sx={{ px: 0, pt: 0 }} value="sv">
                      <Box sx={{ p: 2 }}>
                        <RichTextEditor
                          value={zoneInfoDetails.text_sv ?? ''}
                          setEditor={setSvEditor}
                          placeholder={isNewInstance ? t`Start typing here...` : t`No content`}
                          editor={svEditor}
                          removePlugins={['ImageInsert', 'ImageResize', 'ImageUpload']}
                        />
                        <FieldErrors error={fieldErrors.text_sv} />
                      </Box>
                    </TabPanel>
                    <TabPanel sx={{ px: 0, pt: 0 }} value="en">
                      <Box sx={{ p: 2 }}>
                        <RichTextEditor
                          value={zoneInfoDetails.text_en ?? ''}
                          placeholder={isNewInstance ? t`Start typing here...` : t`No content`}
                          setEditor={setEnEditor}
                          editor={enEditor}
                          removePlugins={['ImageInsert', 'ImageResize', 'ImageUpload']}
                        />
                        <FieldErrors error={fieldErrors.text_en} />
                      </Box>
                    </TabPanel>
                  </Box>
                </TabContext>
              </Box>
              <SaveDialog
                open={isSaveDialogOpen}
                handleClose={() => setIsSaveDialogOpen(false)}
                handleSubmit={handleSubmit}
                loading={isSubmitLoading}
                title={t`Save zone info`}
              >
                <Typography component="span" color="text.primary">
                  {t`Are you sure you want to save the zone info?`}
                </Typography>
              </SaveDialog>
              <Box sx={{ my: 4 }}>
                <CustomLoadingButton
                  isLoading={isSubmitLoading}
                  onClick={onSaveClick}
                  size="large"
                  iconPosition="end"
                >
                  {t`Save zone info`}
                </CustomLoadingButton>
              </Box>
            </StyledForm>
          </Grid>
        </Grid>
      </WithLoader>
    </Box>
  )
}
